import React, { useMemo } from 'react';
import {
  UserGroupIcon,
  DocumentTextIcon,
  CurrencyDollarIcon,
  ChartBarIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  CubeIcon,
  Cog6ToothIcon,
  ChatBubbleLeftRightIcon,
  BoltIcon,
  ReceiptPercentIcon,
  SparklesIcon,
  CpuChipIcon,
  DocumentDuplicateIcon,
  BriefcaseIcon
} from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';
import CommonPageHeader from '../components/common/CommonPageHeader';
import { useQuotations } from '../hooks/useQuotations';
import { useCustomers } from '../hooks/useCustomers';
import { useLanguage } from '../contexts/LanguageContext';
import { useContracts } from 'hooks/useContracts';
import { useAuth } from '../contexts/AuthContext';
import { format } from 'date-fns';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';

const Dashboard = () => {
  const { quotations } = useQuotations();
  const { customers } = useCustomers();
  const { contracts } = useContracts();
  const { t } = useLanguage();
  const { user } = useAuth();

  // Quick navigation tiles configuration
  const quickNavTiles = [
    {
      name: 'Ügyfelek',
      href: '/customers',
      icon: UserGroupIcon,
      color: 'from-blue-500 to-blue-600',
      description: 'Ügyfélkezelés',
      count: customers.length
    },
    {
      name: 'Árajánlatok',
      href: '/quotations',
      icon: DocumentTextIcon,
      color: 'from-green-500 to-green-600',
      description: 'Ajánlatok kezelése',
      count: quotations.length
    },
    {
      name: 'Szerződések',
      href: '/contracts',
      icon: DocumentTextIcon,
      color: 'from-purple-500 to-purple-600',
      description: 'Szerződéskezelés',
      count: contracts.length
    },
    {
      name: 'Termékek',
      href: '/products',
      icon: CubeIcon,
      color: 'from-orange-500 to-orange-600',
      description: 'Termékkatálógus',
      count: null
    },
    {
      name: 'Ügyletek',
      href: '/deals',
      icon: BriefcaseIcon,
      color: 'from-indigo-500 to-indigo-600',
      description: 'Üzleti lehetőségek',
      count: null
    },
    {
      name: 'Kapcsolattartás',
      href: '/customer-relationship-logs',
      icon: ChatBubbleLeftRightIcon,
      color: 'from-teal-500 to-teal-600',
      description: 'Ügyfél napló',
      count: null
    },
    {
      name: 'Energia Dashboard',
      href: '/energia-dashboard',
      icon: ChartBarIcon,
      color: 'from-emerald-500 to-emerald-600',
      description: 'Energia áttekintés',
      count: null
    },
    {
      name: 'POD Kezelés',
      href: '/energia-pods',
      icon: BoltIcon,
      color: 'from-yellow-500 to-yellow-600',
      description: 'Fogyasztási helyek',
      count: null
    },
    {
      name: 'Számlázás',
      href: '/energia-billing',
      icon: ReceiptPercentIcon,
      color: 'from-red-500 to-red-600',
      description: 'Energia számlázás',
      count: null
    },
    {
      name: 'Analitika',
      href: '/energia-analytics',
      icon: SparklesIcon,
      color: 'from-pink-500 to-pink-600',
      description: 'Adatelemzés',
      count: null
    },
    {
      name: 'Monitoring',
      href: '/energia-monitoring',
      icon: CpuChipIcon,
      color: 'from-cyan-500 to-cyan-600',
      description: 'Rendszer figyelés',
      count: null
    },
    {
      name: 'Beállítások',
      href: '/settings',
      icon: Cog6ToothIcon,
      color: 'from-gray-500 to-gray-600',
      description: 'Rendszer beállítások',
      count: null
    }
  ];

  // Aktuális időszak meghatározása a megfelelő üdvözléshez
  const currentHour = new Date().getHours();
  let greeting = t('dashboard.welcome.afternoon');
  
  if (currentHour < 12) {
    greeting = t('dashboard.welcome.morning');
  } else if (currentHour < 18) {
    greeting = t('dashboard.welcome.afternoon');
  } else {
    greeting = t('dashboard.welcome.evening');
  }

  const stats = [
    {
      name: `${greeting} ${user?.displayName || ''}`,
      value: t('dashboard.welcome.message'),
      icon: UserGroupIcon,
      isGreeting: true,
    },
    {
      name: t('dashboard.stats.totalQuotations'),
      value: quotations.length,
      icon: DocumentTextIcon,
      //change: '+12.5%',
      //trend: 'up'
    },
    {
      name: t('dashboard.stats.activeCustomers'),
      value: customers.length,
      icon: UserGroupIcon,
      //change: '+5.2%',
      //trend: 'up'
    },
    {
      name: t('dashboard.stats.totalContracts'),
      value: contracts.length,
      icon: ChartBarIcon,
      //change: '-2.3%',
      //trend: 'down'
    },
  ];

  const recentQuotations = quotations
    .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime())
    .slice(0, 5);

  return (
    <div className="space-y-6 pb-20 sm:pb-0">
      <CommonPageHeader
        title={t('dashboard.title')}
        subtitle={t('dashboard.subtitle')}
        icon={ChartBarIcon}
        showLastUpdate={false}
      />

      {/* Quick Navigation Tiles */}
      <div className="bg-white rounded-xl p-6 shadow-md border border-gray-100">
        <h2 className="text-lg font-semibold text-gray-800 mb-4">Gyors navigáció</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
          {quickNavTiles.map((tile) => (
            <Link
              key={tile.name}
              to={tile.href}
              className="group relative overflow-hidden bg-gradient-to-br from-white to-gray-50 rounded-lg p-4 border border-gray-200 hover:shadow-lg hover:scale-105 transition-all duration-200 hover:border-gray-300"
            >
              {/* Gradient overlay on hover */}
              <div className={`absolute inset-0 bg-gradient-to-br ${tile.color} opacity-0 group-hover:opacity-10 transition-opacity duration-200`}></div>
              
              <div className="relative z-10 flex flex-col items-center text-center space-y-3">
                {/* Icon */}
                <div className={`p-3 rounded-lg bg-gradient-to-br ${tile.color} text-white shadow-md group-hover:shadow-lg transition-shadow duration-200`}>
                  <tile.icon className="w-6 h-6" />
                </div>
                
                {/* Title and description */}
                <div>
                  <h3 className="font-medium text-gray-800 text-sm group-hover:text-gray-900">
                    {tile.name}
                  </h3>
                  <p className="text-xs text-gray-500 mt-1">
                    {tile.description}
                  </p>
                  {tile.count !== null && (
                    <span className="inline-block mt-1 px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full">
                      {tile.count}
                    </span>
                  )}
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat) => (
          <div 
            key={stat.name} 
            className={`rounded-xl p-6 shadow-md ${stat.isGreeting 
              ? 'bg-gradient-to-r from-primary-100 to-primary-50 border border-primary-200' 
              : 'bg-white'
            }`}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className={`${stat.isGreeting ? 'text-primary-700 font-medium' : 'text-gray-600'} text-sm`}>
                  {stat.name}
                </p>
                <p className={`${stat.isGreeting ? 'text-gray-800 text-xl' : 'text-gray-800 text-2xl'} font-semibold mt-1`}>
                  {stat.value}
                </p>
              </div>
              <div className={`p-3 rounded-lg ${stat.isGreeting ? 'bg-primary-100' : 'bg-gray-100'}`}>
                <stat.icon className={`w-6 h-6 ${stat.isGreeting ? 'text-primary-600' : 'text-gray-600'}`} />
              </div>
            </div>
            <div className="flex items-center mt-4">
              {/*{stat.trend === 'up' ? (
                <ArrowUpIcon className="w-4 h-4 text-green-400" />
              ) : (
                <ArrowDownIcon className="w-4 h-4 text-red-400" />
              )}*/}
              <span className={`text-sm ml-1`}>
                {/*{stat.change}*/}
              </span>
              {/*<span className="text-sm text-gray-400 ml-2">{t('dashboard.vsLastMonth')}</span>*/}
            </div>
          </div>
        ))}
      </div>

      {/* Charts & Tables Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">

        {/* Recent Quotations */}
        <div className="bg-white rounded-xl p-6 shadow-md">
          <h2 className="text-lg font-medium text-gray-800 mb-4">{t('dashboard.recentQuotations')}</h2>
          <div className="space-y-4">
            {recentQuotations.map((quote) => (
              <div key={quote.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
                <div>
                  <p className="font-medium text-gray-800">{quote.number}</p>
                  <p className="text-sm text-gray-600">
                    {format(quote.createdAt, 'MMM d, yyyy')}
                  </p>
                  <p className="text-xs text-gray-500">
                    {(() => {
                      // Ügyfél keresése a customerId alapján
                      const customer = customers.find(c => c.id === quote.customerId);
                      if (customer) {
                        // Ha szervezet típusú ügyfél
                        if (customer.type === 'organization') {
                          return customer.companyName;
                        }
                        // Ha magánszemély
                        return `${customer.firstName} ${customer.lastName}`;
                      }
                      return quote.customerName || 'Ismeretlen ügyfél';
                    })()}
                  </p>
                </div>
                <div className="text-right">
                  <p className="font-medium text-gray-800">
                    {(() => {
                      // Áramköltség számítás
                      if (quote.price && quote.consumption) {
                        try {
                          const price = parseFloat(quote.price);
                          const consumption = parseFloat(quote.consumption);
                          let totalCost = 0;
                          let currency = 'HUF';
                          
                          // Ellenőrzöm az árformátumot és számolom a teljes költséget
                          if (quote.priceUnit?.includes('EUR/MWh')) {
                            // EUR/MWh formátum - a fogyasztást MWh-ba kell konvertálni
                            totalCost = price * (consumption / 1000);
                            currency = 'EUR';
                          } else if (quote.priceUnit?.includes('HUF/kWh')) {
                            // HUF/kWh formátum - közvetlen szorzás
                            totalCost = price * consumption;
                            currency = 'HUF';
                          }
                          
                          return new Intl.NumberFormat('hu-HU', {
                            style: 'currency',
                            currency: currency,
                            maximumFractionDigits: 2
                          }).format(totalCost);
                        } catch (e) {
                          return "Nem számítható";
                        }
                      } else {
                        // Ha nincs áramár és fogyasztás adat, akkor a tételek összegét számoljuk
                        return new Intl.NumberFormat('hu-HU', {
                          style: 'currency',
                          currency: 'HUF'
                        }).format(quote.items.reduce((sum, item) =>
                          sum + (item.price * item.quantity * (1 + item.taxRate / 100)), 0
                        ));
                      }
                    })()}
                  </p>
                  <span className={`text-sm px-2 py-1 rounded-full ${quote.status === 'success'
                    ? 'bg-green-500/20 text-green-400'
                    : quote.status === 'pending'
                      ? 'bg-yellow-500/20 text-yellow-400'
                      : 'bg-gray-500/20 text-gray-400'
                    }`}>
                    {t(`quotations.status.${quote.status}`)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Hamarosan lejáró szerződések */}
        <div className="bg-white rounded-xl p-6 shadow-md">
          <h2 className="text-lg font-medium text-gray-800 mb-4">{t('dashboard.expiringContracts.title')}</h2>
          <div className="space-y-4">
            {(() => {
              const today = new Date();
              
              // Hamarosan lejáró szerződések (60 napon belül)
              const soonExpiringContracts = contracts
                .filter(contract => {
                  try {
                    // Ha van validEnd, akkor azt használjuk
                    if (contract.validEnd) {
                      const endDate = new Date(contract.validEnd);
                      // Különbség napokban
                      const diffTime = endDate.getTime() - today.getTime();
                      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                      // Csak a jövőbeni, 60 napon belüli lejáratokat jelenítjük meg
                      return diffDays > 0 && diffDays <= 60;
                    }
                    return false;
                  } catch (error) {
                    return false;
                  }
                })
                .sort((a, b) => {
                  // Rendezés a lejárati dátum alapján (növekvő)
                  const dateA = new Date(a.validEnd || 0);
                  const dateB = new Date(b.validEnd || 0);
                  return dateA.getTime() - dateB.getTime();
                })
                .slice(0, 5); // Maximum 5 elemű lista
              
              if (soonExpiringContracts.length === 0) {
                return (
                  <div className="text-center p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <p className="text-gray-600">{t('dashboard.expiringContracts.noContracts')}</p>
                  </div>
                );
              }
              
              return soonExpiringContracts.map(contract => {
                // Kiszámoljuk a hátralevő napok számát
                let endDate = new Date();
                try {
                  endDate = new Date(contract.validEnd || '');
                } catch (e) {
                  // Hibás dátum esetén marad a mai nap
                }
                
                const diffTime = endDate.getTime() - today.getTime();
                const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                
                // Az ügyfél nevének meghatározása
                const customer = customers.find(c => c.id === contract.customerId);
                let customerName = 'Ismeretlen ügyfél';
                
                if (customer) {
                  if (customer.type === 'organization') {
                    customerName = customer.companyName || 'Ismeretlen szervezet';
                  } else {
                    customerName = `${customer.firstName} ${customer.lastName}`.trim() || 'Ismeretlen ügyfél';
                  }
                }
                
                // A hátralevő napok szövege és színei
                let daysLeftText = t('dashboard.expiringContracts.days', { count: diffDays });
                let bgColor = 'bg-green-100';
                let textColor = 'text-green-600';
                
                if (diffDays <= 7) {
                  bgColor = 'bg-red-100';
                  textColor = 'text-red-600';
                } else if (diffDays <= 30) {
                  bgColor = 'bg-yellow-100';
                  textColor = 'text-yellow-600';
                }
                
                return (
                  <div 
                    key={contract.id} 
                    className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200"
                  >
                    <div>
                      <p className="font-medium text-gray-800">{contract.name}</p>
                      <p className="text-sm text-gray-600">
                        {format(endDate, 'yyyy-MM-dd')}
                      </p>
                      <div className="flex items-center space-x-2 text-xs">
                        <p className="text-gray-500">{customerName}</p>
                        <span className="px-1.5 py-0.5 rounded-md bg-gray-200 text-gray-600 text-[10px] uppercase">
                          {t(`dashboard.expiringContracts.type.${contract.type || 'contract'}`)}
                        </span>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className={`text-sm px-2 py-1 rounded-full ${bgColor} ${textColor}`}>
                        {daysLeftText}
                      </span>
                    </div>
                  </div>
                );
              });
            })()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard; 